import { supabase } from '@/integrations/supabase/client';

interface LoanOfficerInfo {
  id?: string | null;
  name?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  license?: string | null;
}

interface RealtorInfo {
  user_id?: string | null;
  name?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
}

interface LeadData {
  name: string;
  email: string;
  phone?: string | null;
  message?: string | null;
  site_id: string;
  town_id?: string | null;
  area_id?: string | null;
  page_path?: string | null;
  request_type?: string | null;
  site_domain?: string | null;
  town_name?: string | null;
  area_name?: string | null;
  ad_user_data?: string;
  ad_personalization?: string;
  category?: string[];
  loan_officer?: LoanOfficerInfo | null;
  realtor?: RealtorInfo | null;
}


/**
 * Forwards lead data to the external API via edge function.
 * This runs in the background and doesn't block form submission.
 */

/**
 * Forwards lead data to the external API via edge function.
 * This runs in the background and doesn't block form submission.
 */
export async function forwardLead(leadData: LeadData): Promise<void> {
  try {
    // Build the comment field by combining message, town_name, and area_name
    const commentParts: string[] = [];
    if (leadData.message) commentParts.push(leadData.message);
    if (leadData.town_name) commentParts.push(`Town: ${leadData.town_name}`);
    if (leadData.area_name) commentParts.push(`Area: ${leadData.area_name}`);
    const comment = commentParts.length > 0 ? commentParts.join(' | ') : null;

    // Transform to new API format
    const payload = {
      name: leadData.name,
      email: leadData.email,
      phone: leadData.phone || null,
      message: leadData.message || null,
      source: 'digital_real_estate',
      source_domain: leadData.site_domain || null,
      source_page: leadData.page_path || null,
      request_type: leadData.request_type || 'contact',
      town_name: leadData.town_name || null,
      area_name: leadData.area_name || null,
      ad_user_data: leadData.ad_user_data || 'granted',
      ad_personalization: leadData.ad_personalization || 'granted',
      category: leadData.category || [],
      // Include combined comment for backend
      comment,
      // Loan officer info
      loan_officer_id: leadData.loan_officer?.id || null,
      loan_officer_name: leadData.loan_officer?.name || null,
      loan_officer_company: leadData.loan_officer?.company || null,
      loan_officer_email: leadData.loan_officer?.email || null,
      loan_officer_phone: leadData.loan_officer?.phone || null,
      loan_officer_license: leadData.loan_officer?.license || null,
      // Realtor info
      realtor_user_id: leadData.realtor?.user_id || null,
      realtor_name: leadData.realtor?.name || null,
      realtor_company: leadData.realtor?.company || null,
      realtor_email: leadData.realtor?.email || null,
      realtor_phone: leadData.realtor?.phone || null,
    };

    // Forward to external API
    const { data, error } = await supabase.functions.invoke('forward-lead', {
      body: payload,
    });

    if (error) {
      console.error('Failed to forward lead:', error);
    } else if (data?.forwarded) {
      console.log('Lead successfully forwarded to external API');
    } else {
      console.log('Lead not forwarded:', data?.reason || 'Unknown reason');
    }

    // Send email notification (fire-and-forget)
    supabase.functions.invoke('send-lead-notification', {
      body: {
        siteId: leadData.site_id,
        leadData: {
          name: leadData.name,
          email: leadData.email,
          phone: leadData.phone,
          message: leadData.message,
          request_type: leadData.request_type,
          page_path: leadData.page_path,
          site_domain: leadData.site_domain,
          town_id: leadData.town_id,
          area_id: leadData.area_id,
          town_name: leadData.town_name,
          area_name: leadData.area_name,
          category: leadData.category,
          // Loan officer info (no ID in email)
          loan_officer_name: leadData.loan_officer?.name,
          loan_officer_company: leadData.loan_officer?.company,
          loan_officer_email: leadData.loan_officer?.email,
          loan_officer_phone: leadData.loan_officer?.phone,
          loan_officer_license: leadData.loan_officer?.license,
          // Realtor info (no ID in email)
          realtor_name: leadData.realtor?.name,
          realtor_company: leadData.realtor?.company,
          realtor_email: leadData.realtor?.email,
          realtor_phone: leadData.realtor?.phone,
          created_at: new Date().toISOString(),
        },
      },
    }).then(({ error }) => {
      if (error) {
        console.error('Failed to send lead notification email:', error);
      } else {
        console.log('Lead notification email sent');
      }
    });

  } catch (error) {
    // Don't throw - this should not block the main flow
    console.error('Error in forwardLead:', error);
  }
}
